import React, { useState } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { useLocation } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { saveSearchAlert } from '../../../util/api';

import { Form, Modal, NamedLink, PrimaryButton } from '../../../components';

import css from './SavedSearchAlertCard.module.css';

const BellIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />
  </svg>
);

const LockIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <rect x="5" y="10" width="14" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path d="m5 12 4 4L19 6" />
  </svg>
);

const MailIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

const SavedSearchAlertCard = props => {
  const {
    apiSearchParams,
    criteria,
    currentUser,
    onManageDisableScrolling,
    onRemoveCriterion,
    onSearchMissOutcome,
  } = props;
  const intl = useIntl();
  const location = useLocation();
  const [hidden, setHidden] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [savedDuplicate, setSavedDuplicate] = useState(false);
  const [error, setError] = useState(false);
  const isAuthenticated = Boolean(currentUser?.id?.uuid);
  const email = currentUser?.attributes?.email || '';
  const from = `${location.pathname}${location.search}${location.hash}`;

  if (hidden || criteria.length === 0) return null;

  const handleSubmit = async values => {
    if (!values?.emailConsent?.includes('accepted')) return;
    if (!isAuthenticated) {
      setAuthModalOpen(true);
      return;
    }

    setError(false);
    try {
      const response = await saveSearchAlert({
        queryParams: apiSearchParams,
        displayCriteria: criteria,
        searchPath: from,
      });
      onSearchMissOutcome?.('subscribed');
      setSavedDuplicate(Boolean(response?.duplicate));
      setSuccessModalOpen(true);
    } catch (requestError) {
      setError(true);
    }
  };

  const leadingKeyword = criteria.find(item => item.key === 'keywords')?.value;

  return (
    <>
      <section className={css.card} aria-labelledby="savedSearchAlertTitle">
        <div className={css.bellCircle}>
          <BellIcon className={css.bellIcon} />
        </div>
        <h2 id="savedSearchAlertTitle" className={css.title}>
          <FormattedMessage
            id="SavedSearchAlertCard.title"
            values={{ keyword: leadingKeyword || criteria[0]?.value }}
          />
        </h2>
        <p className={css.message}>
          <FormattedMessage id="SavedSearchAlertCard.message" />
        </p>

        <div className={css.chips}>
          {criteria.map(criterion => (
            <span className={css.chip} key={criterion.key}>
              <span>{criterion.label}: <strong>{criterion.value}</strong></span>
              <button
                type="button"
                className={css.removeChip}
                onClick={() => onRemoveCriterion(criterion.key)}
                aria-label={intl.formatMessage(
                  { id: 'SavedSearchAlertCard.removeCriterion' },
                  { label: criterion.label }
                )}
              >
                <svg className={css.removeIcon} viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m6 6 12 12M18 6 6 18" />
                </svg>
              </button>
            </span>
          ))}
        </div>

        <FinalForm
          onSubmit={handleSubmit}
          initialValues={{ emailConsent: [] }}
          render={({ handleSubmit, submitting, values }) => {
            const consented = values?.emailConsent?.includes('accepted');
            return (
              <Form className={css.form} onSubmit={handleSubmit}>
                {isAuthenticated ? (
                  <>
                    <label className={css.emailLabel} htmlFor="savedSearchEmail">
                      <FormattedMessage id="SavedSearchAlertCard.emailLabel" />
                    </label>
                    <div className={css.emailField}>
                      <MailIcon className={css.mailIcon} />
                      <input className={css.emailInput} id="savedSearchEmail" type="email" value={email} readOnly />
                    </div>
                  </>
                ) : null}
                <Field name="emailConsent" type="checkbox" value="accepted">
                  {({ input }) => (
                    <label className={css.consent} htmlFor="savedSearchEmailConsent">
                      <input
                        {...input}
                        type="checkbox"
                        id="savedSearchEmailConsent"
                        className={css.consentInput}
                      />
                      <span className={css.consentText}>
                        <FormattedMessage id="SavedSearchAlertCard.consentLabel" />
                      </span>
                    </label>
                  )}
                </Field>
                {error ? (
                  <p className={css.error} role="alert">
                    <FormattedMessage id="SavedSearchAlertCard.error" />
                  </p>
                ) : null}
                <div className={css.actions}>
                  <PrimaryButton className={css.submitButton} type="submit" disabled={!consented || submitting}>
                    <FormattedMessage
                      id={submitting ? 'SavedSearchAlertCard.saving' : 'SavedSearchAlertCard.submit'}
                    />
                  </PrimaryButton>
                  <button
                    type="button"
                    className={css.noThanks}
                    onClick={() => {
                      onSearchMissOutcome?.('dismissed');
                      setHidden(true);
                    }}
                  >
                    <FormattedMessage id="SavedSearchAlertCard.noThanks" />
                  </button>
                </div>
              </Form>
            );
          }}
        />
      </section>

      <Modal
        id="SavedSearchAuthenticationModal"
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        contentClassName={css.modalContent}
        usePortal
      >
        <div className={css.lockCircle}><LockIcon className={css.modalIcon} /></div>
        <h2 className={css.modalTitle}><FormattedMessage id="SavedSearchAuthModal.title" /></h2>
        <p className={css.modalMessage}><FormattedMessage id="SavedSearchAuthModal.message" /></p>
        <div className={css.modalActions}>
          <NamedLink name="LoginPage" className={css.primaryLink} to={{ state: { from } }}>
            <FormattedMessage id="SavedSearchAuthModal.login" />
          </NamedLink>
          <NamedLink name="SignupPage" className={css.secondaryLink} to={{ state: { from } }}>
            <FormattedMessage id="SavedSearchAuthModal.register" />
          </NamedLink>
        </div>
        <p className={css.modalHelper}><FormattedMessage id="SavedSearchAuthModal.returnMessage" /></p>
      </Modal>

      <Modal
        id="SavedSearchSuccessModal"
        isOpen={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        contentClassName={css.modalContent}
        usePortal
      >
        <div className={css.successCircle}><CheckIcon className={css.modalIcon} /></div>
        <h2 className={css.modalTitle}>
          <FormattedMessage id={savedDuplicate ? 'SavedSearchSuccessModal.duplicateTitle' : 'SavedSearchSuccessModal.title'} />
        </h2>
        <p className={css.modalMessage}>
          <FormattedMessage id={savedDuplicate ? 'SavedSearchSuccessModal.duplicateMessage' : 'SavedSearchSuccessModal.message'} values={{ email: <strong>{email}</strong> }} />
        </p>
        <NamedLink name="SavedSearchesPage" className={css.manageLink}>
          <FormattedMessage id="SavedSearchSuccessModal.manage" />
        </NamedLink>
      </Modal>
    </>
  );
};

export default SavedSearchAlertCard;
